<template>
  <tr
    class="hover:bg-gray-100 transition-colors duration-150 ease-in-out text-right child:py-4 first-child:pl-16 last-child:pr-3 text-sm cursor-pointer"
    v-bind="$attrs"
    :class="
      {
        'bg-gray-50 border-gray-100': depth <= 1,
        'bg-gray-200 border-gray-300': depth === 2,
        'bg-gray-300 border-gray-400': depth === 3,
        'bg-gray-400 border-gray-500': depth >= 4
      }
    "
    @click="showChildren = !showChildren"
  >
    <td
      class="text-left"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.billNumber') + ': ' + tradeItem.billNumber + ', ' + $t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.tradeItemName') + ': ' + tradeItem.name"
    >
      <div
        class="flex items-start space-x-2 space-y-1"
        :style="{ paddingLeft: `${(depth * 20) + 8}px` }"
      >
        <button
          v-if="tradeItem.has_children"
          data-test="open-trade-item"
          type="button"
          class="btn-icon btn-icon-default"
          @click.stop="showChildren = !showChildren"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
            :class="[{'rotate-90': showChildren}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <span class="font-semibold text-gray-900">{{ $props.tradeItem.billNumber }}</span>

        <p class="w-60 text-gray-700">
          {{ tradeItem.name }}
        </p>
      </div>
    </td>

    <td
      class="pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.budgetAmount') + ': ' + $tcc(childBudget)"
    >
      <div v-if="childBudget !== 0">
        <span
          v-if="childBudget - childAdjustedBudget !== 0"
          class="text-gray-600"
        >Works:</span> {{ $tcc(childBudget) }}
      </div>

      <div v-if="childBudget - childAdjustedBudget !== 0">
        <span
          v-if="childBudget !== 0"
          class="text-gray-600"
        >Adjustments:</span> {{ $tcc(-(childBudget - childAdjustedBudget)) }}
      </div>
    </td>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(childSubcontractClaim) }}</span>
    </td>

    <template v-if="claim.capture_claim_amount">
      <td class="bg-green-100/50">
        {{ hasValidBudget ? $tcc(childClaim) : '' }}
      </td>
    </template>

    <td class="bg-green-100/50">
      <span v-if="showSubcontractSummary">{{ $tcc(childSubcontractCertified) }}</span>
    </td>

    <td class="bg-green-100/50">
      {{ hasValidBudget ? $tcc(childCertified) : '' }}
    </td>

    <td
      v-if="claim.capture_claim_amount"
      class="bg-green-100/50"
      :class="{ 'text-red-600': hasVariance && variance !== 0 }"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.varianceAmount') + ': ' + (hasVariance ? $tcc(variance) : '')"
    >
      {{ hasVariance ? $tcc(variance) : '' }}
    </td>

    <td
      class="bg-green-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.previousAmount') + ': ' + (hasValidBudget ? $tcc(childPrevious) : '')"
    >
      {{ hasValidBudget ? $tcc(childPrevious) : '' }}
    </td>

    <td
      class="bg-green-100/50 pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.cumulativeAmount') + ': ' + (hasValidBudget ? $tcc(childPrevious + childCertified) : '')"
    >
      {{ hasValidBudget ? $tcc(childPrevious + childCertified) : '' }}
    </td>

    <template v-if="claim.capture_claim_amount">
      <td
        class="bg-blue-100/50"
        :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.claimPercentage') + ': ' + (hasValidBudget ? `${$tn((childClaim / childBudget) * 100)}%` : '')"
      >
        <span v-if="showPercentages">
          {{ hasValidBudget ? `${$tn((childClaim / childBudget) * 100)}%` : '' }}
        </span>
      </td>
    </template>

    <td
      v-if="!claim.capture_claim_amount || showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.certifiedPercentage') + ': ' + (hasValidBudget ? `${$tn((childCertified / childBudget) * 100)}%` : '')"
    >
      <span v-if="showPercentages">
        {{ hasValidBudget ? `${$tn((childCertified / childBudget) * 100)}%` : '' }}
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.previousPercentage') + ': ' + (hasValidBudget ? $tn((childPrevious / childBudget) * 100) + '%' : '')"
    >
      {{ hasValidBudget ? `${$tn((childPrevious / childBudget) * 100)}%` : '' }}
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.cumulativePercentage') + ': ' + (hasValidBudget ? $tn(((childPrevious + childCertified) / childBudget) * 100) + '%' : '')"
    >
      {{ hasValidBudget ? `${$tn(((childPrevious + childCertified) / childBudget) * 100)}%` : '' }}
    </td>

    <td
      class="pr-3"
      :aria-label="$t('HeadContract.ProgressClaimsLite.TradeRowItem.accessibility.remainingAmount') + ': ' + (hasValidBudget ? $tcc(Math.abs(childBudget - childPrevious - childCertified)) : '')"
    >
      {{ hasValidBudget ? $tcc(Math.max(childBudget - childPrevious - childCertified, 0)) : '' }}
    </td>
  </tr>

  <template v-if="tradeItems.length > 0 && showChildren">
    <TradeRowItem
      v-for="childItem in tradeItems"
      :key="childItem.id"
      :trade="$props.trade"
      :claim="$props.claim"
      :trade-item="childItem"
      :depth="depth ? depth + 1 : 1"
      :show-percentages="$props.showPercentages"
      :show-subcontract-summary="$props.showSubcontractSummary"
      :lump-sum-cost-ids-for-claim-period="props.lumpSumCostIdsForClaimPeriod"
      :rates-cost-ids-for-claim-period="props.ratesCostIdsForClaimPeriod"
    />
  </template>
</template>

<script setup lang="ts">
import Claim from '@/models/Claim';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useTradeItemsStore } from '@/store/tradeItems';
import { ref } from 'vue';
import { computed } from 'vue';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  claim: Claim;
  tradeItem: TradeItem;
  depth?: number;
  showPercentages?: boolean;
  showSubcontractSummary?: boolean;
  lumpSumCostIdsForClaimPeriod: Id[];
  ratesCostIdsForClaimPeriod: Id[];
}>();

defineOptions({
  inheritAttrs: false,
});

const variance = computed(() => {
  return props.tradeItem.childVariance(props.claim);
});

const hasVariance = computed(() => {
  return hasValidBudget.value && variance.value !== 0;
});

const tradeItemsStore = useTradeItemsStore();

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .where('parent_id', props.tradeItem.id)
    .with('package_trade_items', (query) => {
      query.with('package');
    })
    .orderBy('sort_order')
    .get();
});

const showChildren = ref(false);

const childBudget = computed(() => {
  return props.tradeItem.childBudget();
});

const childAdjustedBudget = computed(() => {
  return props.tradeItem.childAdjustedBudget();
});

const hasValidBudget = computed(() => childBudget.value > 0);

const childClaim = computed(() => {
  return props.tradeItem.childClaim(props.claim);
});

const childPrevious = computed(() => {
  return props.tradeItem.childPrevious(props.claim);
});

const childCertified = computed(() => {
  return props.tradeItem.childCertified(props.claim);
});

const childSubcontractClaim = computed(() => {
  return props.tradeItem.childSubcontractSummaryClaim(
    props.lumpSumCostIdsForClaimPeriod,
    props.ratesCostIdsForClaimPeriod,
  );
});

const childSubcontractCertified = computed(() => {
  return props.tradeItem.childSubcontractSummaryCertified(
    props.lumpSumCostIdsForClaimPeriod,
    props.ratesCostIdsForClaimPeriod,
  );
});
</script>

<style scoped></style>
